import { useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import ReactCalendar from 'react-calendar';
import classNames from 'classnames';
import { Provider } from '@vgtv/api-client';

import { config } from '../../config';
import { formatDate } from '../../helpers/date';
import { Spinner } from '../Spinner/Spinner';

import { CalendarMarker } from './CalendarMarker';

import type {
  CalendarProps as ReactCalendarProps,
  CalendarTileProperties,
  FormatterCallback,
  OnChangeDateCallback,
  ViewCallback,
} from 'react-calendar';
import type { CSSProperties, ReactNode, VFC } from 'react';

import * as styles from './Calendar.module.scss';

// Format day of week to single letter
const formatShortWeekday: FormatterCallback = (locale, date) => {
  return dayjs(date).format('dd').charAt(0);
};

// Norwegian locale adds "." by default after the number
const formatDay: FormatterCallback = (locale, date) => {
  return String(date.getDate());
};

export interface CalendarProps extends ReactCalendarProps {
  onDateChange?: (value: Date) => void;
  onMonthChange?: (value: Date) => void;
  datesToMark?: string[];
  legend?: ReactNode;
  loading?: boolean;
  selectedDate?: Date | null;
}

const Calendar: VFC<CalendarProps> = ({
  onDateChange,
  onMonthChange,
  datesToMark,
  className,
  legend,
  loading,
  selectedDate,
  ...props
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null!);

  const handleDateChange: OnChangeDateCallback = useCallback(
    (newDate) => {
      onDateChange?.(newDate);
    },
    [onDateChange]
  );

  const handleActiveStartDateChange: ViewCallback = useCallback(
    ({ activeStartDate }) => {
      onMonthChange?.(activeStartDate);
    },
    [onMonthChange]
  );

  // Add marker to date when exist in datesToMark
  const tileContent = useCallback(
    ({ date, view }: CalendarTileProperties) => {
      if (
        view === 'month' &&
        datesToMark &&
        datesToMark.includes(formatDate(date))
      ) {
        return <CalendarMarker />;
      }

      return null;
    },
    [datesToMark]
  );

  const locale = config.provider === Provider.VGTV ? 'nb-NO' : 'sv-SE';

  return (
    <div
      className={classNames(styles.wrapper, className)}
      ref={wrapperRef}
      // measure initial calendar height to keep it fixed across all views - month, year etc.
      style={
        {
          '--wrapper-height': wrapperRef.current?.clientHeight
            ? `${wrapperRef.current.clientHeight}px`
            : undefined,
        } as CSSProperties
      }
    >
      {loading && (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      )}
      <ReactCalendar
        className={styles.calendar}
        defaultView="month"
        locale={locale}
        onChange={handleDateChange}
        onActiveStartDateChange={handleActiveStartDateChange}
        formatShortWeekday={formatShortWeekday}
        formatDay={formatDay}
        tileContent={datesToMark && tileContent}
        value={selectedDate}
        {...props}
      />
      {legend && <div className={styles.legend}>{legend}</div>}
    </div>
  );
};

export { Calendar };
